import { type FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import LazyImage from 'dibs-react-lazy-image';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import EnvelopeIcon from 'dibs-icons/exports/legacy/Envelope.ts';
import { DesignerDirectoryImageCarousel } from './DesignerDirectoryImageCarousel.tsx';
import {
    trackViewProfileClick,
    trackContactDesignerClick,
} from 'dibs-interiors-photos/exports/trackingHelpers.ts';

import styles from './styles/DesignerDirectoryFirmTile.scss';

import { type DesignerDirectoryFirmTile_firm$data } from './__generated__/DesignerDirectoryFirmTile_firm.graphql.ts';

type Props = {
    handleContact: (arg: string | null) => void;
    firm: DesignerDirectoryFirmTile_firm$data;
};

const profileUrl = (urlSegments: string | Array<string>): string => {
    if (typeof urlSegments === 'string') {
        urlSegments = [urlSegments];
    }

    return `/design-firms/${urlSegments.join('/')}/`;
};

const handleProfileClick = (): void => {
    trackViewProfileClick({ action: 'trade directory' });
};

const DesignerDirectoryFirmTileComponent: FC<Props> = ({ handleContact, firm }) => {
    if (!firm || !firm.directoryProfile || !firm.publicProfile) {
        return null;
    }

    const { directoryProfile, name, publicProfile } = firm;
    const { activeProjectCount, city, country, profilePhoto } = directoryProfile;
    const { email, urlLabel } = publicProfile;

    const profileImage = profilePhoto ? (
        <LazyImage offsetVertical={400} placeholderClass={styles.circleImage}>
            <img
                src={`${profilePhoto}?width=50&crop=50:50&format=pjpg&auto=webp`}
                className={styles.profileImage}
                alt={name || undefined}
                data-tn="design-firms-profile-image"
            />
        </LazyImage>
    ) : null;

    const projectCount = (
        <FormattedMessage
            id="abt.DesignerDirectoryFirmTile.projectCount"
            defaultMessage="{displayCount} {count, plural, one {Project} other {Projects}} on 1stDibs"
            values={{
                displayCount: (
                    <span className={styles.projectCountValue}>
                        <FormattedNumber value={activeProjectCount || 0} />
                    </span>
                ),
                count: activeProjectCount || 0,
            }}
        />
    );

    return (
        <div className={`${styles.wrapper} colSm12 colLg6 colXl4`} data-tn="design-firms-tile">
            <div className={styles.imageWrapper}>
                <DesignerDirectoryImageCarousel firm={firm} />
            </div>
            <div className={styles.firmWrapper}>
                <div>
                    <div className={styles.firmInfoHeader}>
                        {urlLabel ? (
                            <Link
                                className={styles.profileImageWrapper}
                                href={profileUrl(urlLabel)}
                                onClick={handleProfileClick}
                                dataTn="design-firms-firm-profile-image"
                            >
                                {profileImage}
                            </Link>
                        ) : (
                            <span className={styles.profileImageWrapper}>{profileImage}</span>
                        )}
                        <div className={styles.firmInfoText}>
                            {urlLabel ? (
                                <Link
                                    className={styles.firmName}
                                    href={profileUrl(urlLabel)}
                                    onClick={handleProfileClick}
                                    dataTn="design-firms-firm-name"
                                >
                                    {name}
                                </Link>
                            ) : (
                                <span className={styles.firmName} data-tn="design-firms-firm-name">
                                    {name}
                                </span>
                            )}
                            <div
                                className={styles.firmLocation}
                                data-tn="design-firms-firm-location"
                            >
                                {city && country && (
                                    <FormattedMessage
                                        id="abt.DesignerDirectoryFirmTile.location"
                                        defaultMessage="{city}, {country}"
                                        values={{ city, country }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles.projectCountWrapper}>
                        {urlLabel ? (
                            <Link
                                className={styles.projectCount}
                                href={profileUrl([urlLabel, 'projects'])}
                                onClick={handleProfileClick}
                                dataTn="design-firms-project-count"
                            >
                                {projectCount}
                            </Link>
                        ) : (
                            <span className={styles.projectCount}>{projectCount}</span>
                        )}
                    </div>
                </div>
                <div className={styles.firmCtas}>
                    <Link
                        dataTn="design-firms-contact-cta"
                        className={styles.contactCta}
                        onClick={() => {
                            handleContact(email);
                            trackContactDesignerClick({ label: 'directory' });
                        }}
                    >
                        <div className={styles.contactLinkWrapper}>
                            <EnvelopeIcon className={styles.contactSvg} />
                            <FormattedMessage
                                id="abt.DesignerDirectoryFirmTile.contact"
                                defaultMessage="Contact"
                            />
                        </div>
                    </Link>
                    {urlLabel && (
                        <Button
                            dataTn="design-firms-view-profile-cta"
                            className={styles.viewProfileCta}
                            size="small"
                            href={profileUrl(urlLabel)}
                            onClick={handleProfileClick}
                        >
                            <FormattedMessage
                                id="abt.DesignerDirectoryFirmTile.viewProfile"
                                defaultMessage="view profile"
                            />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export const DesignerDirectoryFirmTile = createFragmentContainer(
    DesignerDirectoryFirmTileComponent,
    {
        firm: graphql`
            fragment DesignerDirectoryFirmTile_firm on DirectoryDesignFirm {
                name
                directoryProfile {
                    activeProjectCount
                    city
                    country
                    profilePhoto
                }
                publicProfile {
                    urlLabel
                    email
                }
                ...DesignerDirectoryImageCarousel_firm
            }
        `,
    }
);
